/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { SUPPORTED_LOCALES, TIZEN_PLATFORM } from "../../config";
import { getSupportedLocale } from "../locale/localeResolver";
import { MessageType } from "../enums";
import { MessageService } from "../infrastructure";
import { Log } from "../logging/Log";
import { getOSVersion } from "../utility/getOSVersion";

/**
 * TV Properties received from shell app via postMessage.
 */
interface ITvProperties {
  activeConnectionType?: string;
  advertisingId?: string;
  advertisingTrackingEnabled?: boolean;
  dateLocale?: string;
  deviceModel?: string;
  deviceModelCode?: string;
  deviceName?: string;
  deviceTotalMemory?: string | number;
  hsn?: string;
  ipAddress?: string;
  lang?: string;
  locale?: string;
  macAddress?: string;
  platformName?: string;
  platformVersion?: string;
  region?: string;
  timezone?: string;
}

/**
 * Device info injected by shell app on window.deviceInfo.
 */
interface IWindowDeviceInfo {
  firmware?: string;
  model?: string;
  modelName?: string;
  modelCode?: string;
  osVersion?: string;
}

/**
 * Network Information API (experimental, not in all browsers).
 */
interface INetworkInformation {
  effectiveType?: string;
  type?: string;
}

/**
 * Extended Navigator with experimental APIs.
 */
interface INavigatorExtended extends Navigator {
  connection?: INetworkInformation;
  deviceMemory?: number;
  mozConnection?: INetworkInformation;
  webkitConnection?: INetworkInformation;
}

/**
 * Extended Window with shell app injections.
 */
interface IWindowExtended extends Window {
  deviceId?: string;
  deviceInfo?: string | IWindowDeviceInfo;
  getOSVersion?: () => string;
}

/* -------------------------------------------------------------------------
 * User-agent parsing
 *
 * Split out of TvProperties.parseUserAgent(), which had grown one branch per
 * browser AND one per OS in a single function. These are pure and module-
 * level: each takes the UA string and returns only what it can determine, so
 * a new platform is a new small function plus one line in the dispatcher.
 * ---------------------------------------------------------------------- */

const UNKNOWN = "Unknown";

interface IUaBrowser {
  browserName: string;
  browserVersion: string;
}

interface IUaOs {
  osName: string;
  osVersion: string;
  deviceModel: string;
}

/**
 * Returns capture group `group` of `re` in `ua`, trimmed, or undefined when
 * the pattern does not match or the group is empty.
 */
function uaGroup(ua: string, re: RegExp, group: number): string | undefined {
  const match = ua.match(re);
  if (match === null || !match[group]) return undefined;
  const value = match[group].trim();
  return value === "" ? undefined : value;
}

/** Same as uaGroup() but falls back to "Unknown" rather than undefined. */
function uaVersion(ua: string, re: RegExp): string {
  const value = uaGroup(ua, re, 1);
  return value === undefined ? UNKNOWN : value;
}

/**
 * Browser name and version. Order matters: the Edge UA also contains
 * "Chrome/", and the Safari UA also contains "Safari/" on Chrome.
 */
function parseUaBrowser(ua: string): IUaBrowser {
  if (/Edg\//.test(ua)) {
    return {
      browserName: "Edge",
      browserVersion: uaVersion(ua, /Edg\/(\d+(\.\d+)*)/)
    };
  }
  if (/Chrome\//.test(ua)) {
    return {
      browserName: "Chrome",
      browserVersion: uaVersion(ua, /Chrome\/(\d+(\.\d+)*)/)
    };
  }
  if (/Firefox\//.test(ua)) {
    return {
      browserName: "Firefox",
      browserVersion: uaVersion(ua, /Firefox\/(\d+(\.\d+)*)/)
    };
  }
  if (/Safari\//.test(ua)) {
    return {
      browserName: "Safari",
      browserVersion: uaVersion(ua, /Version\/(\d+(\.\d+)*)/)
    };
  }
  return { browserName: UNKNOWN, browserVersion: UNKNOWN };
}

function parseTizenUa(ua: string): IUaOs {
  const smartTv = uaGroup(ua, /SMART-TV|SmartTV/, 0);
  // eslint-disable-next-line sonarjs/slow-regex
  const build = uaGroup(ua, /;\s*([^;)]+)\s*Build\//, 1);
  return {
    osName: "Tizen",
    osVersion: uaVersion(ua, /Tizen\s*(\d+(\.\d+)*)/),
    deviceModel: smartTv || build || "SmartTV"
  };
}

function parseWebOsUa(ua: string): IUaOs {
  const version = uaGroup(ua, /Web0S[^;]*;\s*([^;)]*)/, 1);
  return {
    osName: "webOS",
    osVersion: version === undefined ? UNKNOWN : version,
    deviceModel: "SmartTV"
  };
}

function parseAndroidUa(ua: string): IUaOs {
  // eslint-disable-next-line sonarjs/slow-regex
  const model = uaGroup(ua, /;\s*([^;)]+)\s*Build\//, 1);
  return {
    osName: "Android",
    osVersion: uaVersion(ua, /Android (\d+(\.\d+)*)/),
    deviceModel: model === undefined ? UNKNOWN : model
  };
}

function parseIosUa(ua: string): IUaOs {
  const raw = uaGroup(ua, /OS (\d+[._]\d+[._]?\d*)/, 1);
  const device = uaGroup(ua, /(iPhone|iPad|iPod)/, 1);
  return {
    osName: "iOS",
    osVersion: raw === undefined ? UNKNOWN : raw.replace(/_/g, "."),
    deviceModel: device === undefined ? UNKNOWN : device
  };
}

function parseMacUa(ua: string): IUaOs {
  const raw = uaGroup(ua, /Mac OS X (\d+[._]\d+[._]?\d*)/, 1);
  return {
    osName: "macOS",
    osVersion: raw === undefined ? UNKNOWN : raw.replace(/_/g, "."),
    deviceModel: UNKNOWN
  };
}

/**
 * OS name, OS version and device model. TV platforms are tested first: a
 * Tizen or webOS UA also contains "Linux".
 */
function parseUaOs(ua: string): IUaOs {
  if (/Tizen/.test(ua)) return parseTizenUa(ua);
  if (/Web0S|webOS/.test(ua)) return parseWebOsUa(ua);
  if (/Windows/.test(ua)) {
    return {
      osName: "Windows",
      osVersion: uaVersion(ua, /Windows NT (\d+\.\d+)/),
      deviceModel: UNKNOWN
    };
  }
  if (/Android/.test(ua)) return parseAndroidUa(ua);
  // iOS BEFORE macOS: every iPhone/iPad/iPod UA contains the literal
  // "like Mac OS X", so testing macOS first made the iOS branch unreachable
  // and reported "macOS" for every iPhone. The reverse cannot happen — a real
  // macOS UA never contains iPhone/iPad/iPod.
  if (/iPhone|iPad|iPod/.test(ua)) return parseIosUa(ua);
  if (/Mac OS X/.test(ua)) return parseMacUa(ua);
  if (/Linux/.test(ua)) {
    return { osName: "Linux", osVersion: UNKNOWN, deviceModel: UNKNOWN };
  }
  return { osName: UNKNOWN, osVersion: UNKNOWN, deviceModel: UNKNOWN };
}

export class TvProperties {
  private static TAG = "TvProperties";

  private oTvProperties: ITvProperties = {};

  private cachedWindowDeviceInfo: IWindowDeviceInfo | null = null;

  private cachedUaParsed: {
    browserName: string;
    browserVersion: string;
    osName: string;
    osVersion: string;
    deviceModel: string;
  } | null = null;

  /**
   * The generated fallback used by {@link getHsn} when neither the shell nor
   * `window.deviceId` supplies one. Cached so repeat calls return the same
   * value instead of a fresh random one each time — callers (e.g. the
   * TiVo+ user-session identifier) depend on it being stable for the life of
   * this instance, not merely present.
   */
  private cachedGeneratedHsn: string | null = null;

  private generateUniqueId(): string {
    let id: string;
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant

    const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0"));

    id =
      hex.slice(0, 4).join("") +
      "-" +
      hex.slice(4, 6).join("") +
      "-" +
      hex.slice(6, 8).join("") +
      "-" +
      hex.slice(8, 10).join("") +
      "-" +
      hex.slice(10, 16).join("");

    // format the user id in below format:
    // 27767c65-f950-4509-86dc-b356848472c9
    const match = id.match(/^(\w{8})(\w{4})(\w{4})(\w{4})(\w+)$/);
    if (match) {
      id = `${match[1]}-${match[2]}-${match[3]}-${match[4]}-${match[5]}`;
    }
    return id;
  }

  /**
   * Parses the `window.deviceInfo` object set by the shell app on the iframe's contentWindow.
   * Available on Tizen/WebOS shell apps that use the contentWindow injection pattern.
   * Used for TMIS
   * @returns Parsed device info object or empty object.
   */
  private getWindowDeviceInfo(): IWindowDeviceInfo {
    if (this.cachedWindowDeviceInfo !== null)
      return this.cachedWindowDeviceInfo;
    try {
      const deviceInfo = (window as IWindowExtended).deviceInfo;
      if (typeof deviceInfo === "string") {
        this.cachedWindowDeviceInfo = JSON.parse(
          deviceInfo
        ) as IWindowDeviceInfo;
      } else if (typeof deviceInfo === "object" && deviceInfo !== null) {
        this.cachedWindowDeviceInfo = deviceInfo;
      } else {
        this.cachedWindowDeviceInfo = {};
      }
    } catch {
      this.cachedWindowDeviceInfo = {};
    }
    return this.cachedWindowDeviceInfo as IWindowDeviceInfo;
  }

  /**
   * Parses browser and OS info from navigator.userAgent.
   * Used for device model, platform name, and version detection.
   */
  private parseUserAgent(): {
    browserName: string;
    browserVersion: string;
    osName: string;
    osVersion: string;
    deviceModel: string;
  } {
    if (this.cachedUaParsed) return this.cachedUaParsed;

    const ua = navigator.userAgent;
    const browser = parseUaBrowser(ua);
    const os = parseUaOs(ua);

    this.cachedUaParsed = {
      browserName: browser.browserName,
      browserVersion: browser.browserVersion,
      osName: os.osName,
      osVersion: os.osVersion,
      deviceModel: os.deviceModel
    };
    return this.cachedUaParsed;
  }

  public disableScreenSaver() {
    if (this.getPlatformName() === TIZEN_PLATFORM) {
      new MessageService(MessageType.DISABLE_SCREENSAVER)
        .getResponse()
        .catch((error) => {
          Log.e(TvProperties.TAG, error);
        });
    }
  }

  public enableScreenSaver() {
    if (this.getPlatformName() === TIZEN_PLATFORM) {
      new MessageService(MessageType.ENABLE_SCREENSAVER)
        .getResponse()
        .catch((error) => {
          Log.e(TvProperties.TAG, error);
        });
    }
  }

  public getActiveConnectionType = () => {
    const nav = navigator as INavigatorExtended;
    const networkInfo =
      nav.connection || nav.mozConnection || nav.webkitConnection;
    const browserConnectionType = networkInfo
      ? networkInfo.type || networkInfo.effectiveType
      : undefined;
    return (
      (this.oTvProperties && this.oTvProperties.activeConnectionType) ||
      browserConnectionType ||
      "Unknown"
    );
  };

  /**
   * Region-only tags such as "es-419" or "es-MX" must still select the Spanish
   * catalogue, so the language is matched rather than the full tag.
   */
  private static toSupportedLocaleTag(locale: string): string {
    const language: string = locale
      .replace("_", "-")
      .split("-")[0]
      .toLowerCase();
    return getSupportedLocale(language).replace("_", "-");
  }

  /**
   * The advertising identifier (e.g. Amazon Advertising ID on Vega/Kepler
   * devices) reported by the shell app. Not derivable from browser APIs, so
   * this is shell-only with no UA-based fallback — empty when the shell
   * hasn't reported one (older shell builds, or tracking disabled, per
   * {@link hasAdvertisingTrackingEnabled}).
   */
  public getAdvertisingId = () => {
    return (this.oTvProperties && this.oTvProperties.advertisingId) || "";
  };

  /**
   * Whether the user has enabled ad tracking on this device, as reported by
   * the shell app. Callers should treat a missing/unreported value as
   * tracking disabled (the safer default) rather than assume consent.
   */
  public hasAdvertisingTrackingEnabled = () => {
    return (
      (this.oTvProperties && this.oTvProperties.advertisingTrackingEnabled) ||
      true
    );
  };

  public getDateLocale = () => {
    const browserLocale =
      navigator.language ||
      (navigator.languages ? navigator.languages[0] : undefined);
    // TODO: '../storage/Storage' module does not exist yet.
    // const storedLocale = Storage.getInstance(Storage.StorageType.LOCAL_STORAGE)
    //   .getItem(Storage.Keys.LOCALE)
    //   ?.replace("_", "-");
    const resolvedLocale =
      // storedLocale ||
      (this.oTvProperties && this.oTvProperties.dateLocale) ||
      browserLocale ||
      "en-US";
    return TvProperties.toSupportedLocaleTag(resolvedLocale);
  };

  /**
   * this function should be used as time zone value when you request any
   * date time related data from service api.
   * service api will then return data according to GMT and when we convert that time
   * into date object using Date constructor, it will automatically take care of
   * converting GMT time to current time zone
   * @returns string {GMT}
   */
  public getDefaultTimeZone = () => {
    return "GMT";
  };

  public getDeviceModel = () => {
    const uaParsed = this.parseUserAgent();
    const parsedModel =
      uaParsed.deviceModel !== "Unknown" ? uaParsed.deviceModel : undefined;
    const windowDeviceInfo = this.getWindowDeviceInfo();
    const windowDeviceModel = windowDeviceInfo
      ? windowDeviceInfo.model || windowDeviceInfo.modelName
      : undefined;
    return (
      (this.oTvProperties && this.oTvProperties.deviceModel) ||
      windowDeviceModel ||
      parsedModel ||
      "Unknown"
    );
  };

  public getDeviceModelCode = () => {
    // TODO: Device model code is not directly available via browser APIs.
    // Priority: window.deviceInfo > shell app postMessage
    const windowDeviceInfo = this.getWindowDeviceInfo();
    const windowDeviceModelCode = windowDeviceInfo
      ? windowDeviceInfo.modelCode
      : undefined;
    return (
      (this.oTvProperties && this.oTvProperties.deviceModelCode) ||
      windowDeviceModelCode ||
      "Unknown"
    );
  };

  public getDeviceName = () => {
    const uaParsed = this.parseUserAgent();
    const browserDeviceName =
      uaParsed.osName !== "Unknown"
        ? `${uaParsed.osName} ${uaParsed.browserName}`
        : undefined;
    const windowDeviceInfo = this.getWindowDeviceInfo();
    const windowDeviceName = windowDeviceInfo
      ? windowDeviceInfo.firmware
      : undefined;
    return (
      (this.oTvProperties && this.oTvProperties.deviceName) ||
      windowDeviceName ||
      browserDeviceName ||
      "Unknown"
    );
  };

  public getDeviceTotalMemory = () => {
    const nav = navigator as INavigatorExtended;
    const browserMemory = nav.deviceMemory ? nav.deviceMemory : undefined;
    return (
      (this.oTvProperties && this.oTvProperties.deviceTotalMemory) ||
      browserMemory ||
      "Unknown"
    );
  };

  public getHsn = () => {
    if (this.cachedGeneratedHsn === null) {
      this.cachedGeneratedHsn = this.generateUniqueId();
    }
    return (
      (this.oTvProperties && this.oTvProperties.hsn) ||
      (window as IWindowExtended).deviceId ||
      this.cachedGeneratedHsn ||
      "Unknown"
    );
  };

  public getIpAddress = () => {
    // TODO: IP address is not accessible via browser APIs for security/privacy reasons.
    return (this.oTvProperties && this.oTvProperties.ipAddress) || "Unknown";
  };

  public getLang = () => {
    const browserLang = navigator.language
      ? navigator.language.split("-")[0]
      : undefined;
    // TODO: '../storage/Storage' module does not exist yet.
    // const storedLang = Storage.getInstance(
    //   Storage.StorageType.LOCAL_STORAGE
    // ).getItem(Storage.Keys.LANGUAGE);
    return (
      // storedLang ||
      (this.oTvProperties && this.oTvProperties.lang) || browserLang || "en"
    );
  };

  public getLocale = () => {
    // Priority: Web browser API > stored locale > window.deviceInfo > shell app postMessage
    const browserLocale =
      (navigator.language ? navigator.language.replace("-", "_") : undefined) ||
      (navigator.languages && navigator.languages[0]
        ? navigator.languages[0].replace("-", "_")
        : undefined);
    // TODO: '../storage/Storage' module does not exist yet.
    // const storedLocale = Storage.getInstance(
    //   Storage.StorageType.LOCAL_STORAGE
    // ).getItem(Storage.Keys.LOCALE);
    return (
      // storedLocale ||
      (this.oTvProperties && this.oTvProperties.locale) ||
      browserLocale ||
      "en_US"
    );
  };

  public getMacAddress() {
    // TODO: MAC address is not accessible via browser APIs for security/privacy reasons.
    // Available through shell app (window.deviceInfo or postMessage) only.
    return (this.oTvProperties && this.oTvProperties.macAddress) || "";
  }

  /**
   * The hyphen-joined OS-version token for the current device — e.g.
   * `Tizen-7.0`, `webOS-23`, `Kepler-1.2`, `Windows-Edge-130.0.0.0`.
   *
   * Exposed here so callers that already hold a TvProperties instance do not
   * each reach for the utility directly. Unlike getPlatformVersion(), this is
   * derived purely from the user agent: it deliberately ignores the shell-app
   * values, because its whole point is one consistently shaped token across
   * every platform.
   *
   * @returns {string} The OS version token, or "Unknown" when the user agent
   * matches no known platform.
   */
  public getOSVersion = () => {
    return getOSVersion();
  };

  /**
   * Retrieves the Olson time zone format (IANA time zone) of the current system.
   *
   * @returns {string} The Olson time zone string (e.g., "Asia/Kolkata", "America/New_York").
   */
  public getOlsonTimeZone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  };

  public getPlatformName() {
    const uaParsed = this.parseUserAgent();
    const browserPlatform =
      uaParsed.osName !== "Unknown" ? uaParsed.osName : undefined;
    return (
      (this.oTvProperties && this.oTvProperties.platformName) ||
      browserPlatform ||
      "Unknown"
    );
  }

  public getPlatformVersion() {
    const uaParsed = this.parseUserAgent();
    const browserVersion =
      uaParsed.osVersion !== "Unknown" ? uaParsed.osVersion : undefined;
    const windowDeviceInfo = this.getWindowDeviceInfo();
    const windowExtended = window as IWindowExtended;
    const windowDeviceVersion =
      (windowDeviceInfo ? windowDeviceInfo.osVersion : undefined) ||
      (windowExtended && typeof windowExtended.getOSVersion === "function"
        ? windowExtended.getOSVersion()
        : undefined);
    return (
      (this.oTvProperties && this.oTvProperties.platformVersion) ||
      windowDeviceVersion ||
      browserVersion ||
      "Unknown"
    );
  }

  public getRegion() {
    const browserRegion = navigator.language
      ? navigator.language.split("-")[1]
      : undefined;
    return (
      (this.oTvProperties && this.oTvProperties.region) || browserRegion || "US"
    );
  }

  /**
   * This function should be used in places where device information is to be provided.
   * Ex. Logging, device info in v1/iptvLogin, etc.
   * @returns string {current time zone of device}
   */
  public getTimezone() {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return (
      (this.oTvProperties && this.oTvProperties.timezone) ||
      browserTimezone ||
      "PDT"
    );
  }

  public hasSupportedLanguage = () => {
    const currentLang = this.getLang();
    const isLanguageSupported = SUPPORTED_LOCALES.some(
      (item) => item.language === currentLang
    );
    return isLanguageSupported;
  };

  public initialize = async (
    skipMessageService: boolean = false
  ): Promise<boolean> => {
    if (skipMessageService) {
      Log.i(
        TvProperties.TAG,
        "initialize(): Skipping MessageService, " + "using window/UA fallbacks."
      );
      return Promise.resolve(true);
    }
    return new Promise((resolve, reject) => {
      new MessageService(MessageType.GET_TV_PROPERTIES)
        .getResponse()
        .then((response) => {
          Log.i(TvProperties.TAG, `Got the TV Properties ${response}`);
          this.oTvProperties = response as ITvProperties;
          if (this.oTvProperties && this.oTvProperties.dateLocale) {
            this.oTvProperties.dateLocale = TvProperties.toSupportedLocaleTag(
              this.oTvProperties.dateLocale
            );
          }
          resolve(true);
        })
        .catch((error) => {
          Log.e(TvProperties.TAG, "Failed to initialize TV Properties.", error);
          reject(error);
        });
    });
  };
}
